import React from 'react';
import styled, { keyframes } from 'styled-components';
import { motion } from 'framer-motion';
import { FaBrain, FaServer, FaShieldAlt, FaBolt } from 'react-icons/fa';

const float = keyframes`
  0% { transform: translateY(0px); }
  50% { transform: translateY(-8px); }
  100% { transform: translateY(0px); }
`;

const shimmer = keyframes`
  0% { background-position: 0% 50%; }
  50% { background-position: 100% 50%; }
  100% { background-position: 0% 50%; }
`;

const borderPulse = keyframes`
  0% { box-shadow: 0 0 0 0 rgba(16, 185, 129, 0.45); }
  70% { box-shadow: 0 0 0 10px rgba(16, 185, 129, 0); }
  100% { box-shadow: 0 0 0 0 rgba(16, 185, 129, 0); }
`;

const HeroContainer = styled.section`
  position: relative;
  overflow: hidden;
  padding: 9rem 1rem 4.5rem;
  background:
    radial-gradient(circle at 85% 12%, rgba(59, 130, 246, 0.14), transparent 38%),
    radial-gradient(circle at 12% 16%, rgba(16, 185, 129, 0.12), transparent 35%),
    linear-gradient(180deg, #fcfeff 0%, #f4f9ff 100%);
`;

const Content = styled.div`
  max-width: 1160px;
  margin: 0 auto;
  display: grid;
  gap: 1.5rem;
  grid-template-columns: 1fr;

  @media (min-width: 992px) {
    grid-template-columns: 1.15fr 0.85fr;
    align-items: center;
  }
`;

const Left = styled.div`
  text-align: left;
`;

const Badge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  font-size: 0.82rem;
  font-weight: 700;
  color: #0f766e;
  background: rgba(16, 185, 129, 0.14);
  border: 1px solid rgba(15, 118, 110, 0.2);
  border-radius: 999px;
  padding: 0.4rem 0.75rem;
  margin-bottom: 1rem;
`;

const FreePill = styled.span`
  display: inline-flex;
  align-items: center;
  margin-left: 0.45rem;
  padding: 0.15rem 0.5rem;
  border-radius: 999px;
  background: #fef3c7;
  color: #92400e;
  font-weight: 800;
  font-size: 0.76rem;
  border: 1px solid #fcd34d;
`;

const Title = styled.h1`
  margin: 0;
  font-size: clamp(1.95rem, 4vw, 3.15rem);
  line-height: 1.1;
  font-weight: 800;
  color: #0b1220;

  span {
    background: linear-gradient(92deg, #1d4ed8, #0f766e, #0369a1);
    background-size: 200% 200%;
    animation: ${shimmer} 6s ease infinite;
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
  }
`;

const Subtitle = styled.p`
  margin: 1rem 0 1.35rem;
  font-size: clamp(1rem, 1.8vw, 1.2rem);
  line-height: 1.65;
  color: #1f2a3c;
  max-width: 740px;
`;

const CTAButton = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  text-decoration: none;
  padding: 0.9rem 1.3rem;
  border-radius: 12px;
  font-weight: 800;
  color: #ffffff;
  background: linear-gradient(90deg, #1d4ed8 0%, #0f766e 100%);
  border: 2px solid rgba(16, 185, 129, 0.95);
  box-shadow: 0 14px 30px rgba(29, 78, 216, 0.22);
  transition: transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease;
  animation: ${borderPulse} 2.2s infinite;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 18px 32px rgba(29, 78, 216, 0.28);
    border-color: #ffffff;
  }
`;

const Note = styled.p`
  margin: 0.7rem 0 0;
  font-size: 0.9rem;
  color: #0f172a;
  font-weight: 700;
`;

const Right = styled.div`
  display: grid;
  gap: 0.75rem;
`;

const InfoCard = styled(motion.div)`
  background: #ffffff;
  border: 1px solid rgba(148, 163, 184, 0.22);
  border-radius: 14px;
  padding: 0.9rem;
  box-shadow: 0 10px 24px rgba(15, 23, 42, 0.07);
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
`;

const IconWrap = styled.div`
  width: 38px;
  height: 38px;
  border-radius: 10px;
  flex-shrink: 0;
  background: linear-gradient(145deg, #dbeafe, #dcfce7);
  color: #0f172a;
  display: flex;
  align-items: center;
  justify-content: center;
  animation: ${float} 3.2s ease-in-out infinite;
`;

const CardTitle = styled.h3`
  margin: 0;
  color: #0f172a;
  font-size: 0.98rem;
  font-weight: 700;
`;

const CardText = styled.p`
  margin: 0.15rem 0 0;
  color: #475569;
  font-size: 0.85rem;
  line-height: 1.45;
`;


const Hero = () => {
  return (
    <HeroContainer id="hero">
      <Content>
        <Left>
          <Badge>
            AI-first schooling for India
            <FreePill>FREE OF COST</FreePill>
          </Badge>
          <Title>
            Let my school be an <span>AI Digital School</span> in 2 minutes
          </Title>
          <Subtitle>
            Why are India schools not moving faster toward AI-ready learning?
            India is moving toward an AI-dominant future. Our kids must learn with modern tools now.
            SKHOOL is built as an India-first AI school management system with free-of-cost onboarding,
            helping schools upgrade quickly with reliable technology, automation, and a student-first digital experience.
          </Subtitle>
          <CTAButton href="https://app.skhool.in/register-school">
            Let My School Be AI Digital
          </CTAButton>
          <Note>Free of cost to get started. No forms here, tap once and onboard your school.</Note>
        </Left>

        <Right>
          <InfoCard whileHover={{ y: -3 }} transition={{ duration: 0.2 }}>
            <IconWrap><FaBolt /></IconWrap>
            <div>
              <CardTitle>Fast School Launch</CardTitle>
              <CardText>Setup classes, sections, subjects, fees, and website defaults in a guided flow.</CardText>
            </div>
          </InfoCard>

          <InfoCard whileHover={{ y: -3 }} transition={{ duration: 0.2 }}>
            <IconWrap><FaBrain /></IconWrap>
            <div>
              <CardTitle>AI for Daily Work</CardTitle>
              <CardText>Automate reminders, reports, follow-ups, and parent communication with smart assistance.</CardText>
            </div>
          </InfoCard>

          <InfoCard whileHover={{ y: -3 }} transition={{ duration: 0.2 }}>
            <IconWrap><FaServer /></IconWrap>
            <div>
              <CardTitle>Reliable Cloud Operations</CardTitle>
              <CardText>Stable cloud setup with monitoring for consistent school operations.</CardText>
            </div>
          </InfoCard>

          <InfoCard whileHover={{ y: -3 }} transition={{ duration: 0.2 }}>
            <IconWrap><FaShieldAlt /></IconWrap>
            <div>
              <CardTitle>Secure by Design</CardTitle>
              <CardText>Role-based access, data protection, and dependable operations for admins, teachers, and parents.</CardText>
            </div>
          </InfoCard>
        </Right>
      </Content>
    </HeroContainer>
  );
};

export default Hero;
